import { useCallback, useRef, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent } from 'src/contexts/ContentContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { downloadFileFromUrl } from 'src/utils/fileUtils';
import {
  AccountingEventClient,
  AccountingEventExportResult,
  Feature,
} from 'src/WebApiController';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 60; // 2 minutes max polling

export interface UseAccountingEventExportParams {
  stream: string;
  createDateFrom?: string;
  createDateTo?: string;
  entityId?: number;
}

export const useAccountingEventExport = ({
  stream,
  createDateFrom,
  createDateTo,
  entityId,
}: UseAccountingEventExportParams) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const hasFeature = useUserHasFeature(Feature.AccountingAuditExport);

  const downloadStartingMsg = useContent(ContentId.ExportDownloadStarting);
  const exportFailedMsg = useContent(ContentId.ExportFailed);
  const exportNoDownloadUrlMsg = useContent(ContentId.ExportNoDownloadUrl);
  const exportPreparingMsg = useContent(ContentId.ExportPreparing);
  const exportProcessingMsg = useContent(ContentId.ExportProcessing);
  const exportTimedOutMsg = useContent(ContentId.ExportTimedOut);

  const [isExporting, setIsExporting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollAttemptsRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollAttemptsRef.current = 0;
  }, []);

  const triggerDownload = useCallback(
    (downloadUrl: string) => {
      setStatusMessage(downloadStartingMsg);
      downloadFileFromUrl('accounting-export.csv', downloadUrl);
    },
    [downloadStartingMsg]
  );

  const pollExport = useCallback(
    (exportId: number) => {
      const client = new AccountingEventClient(activeAccountWebClientConfig);

      const poll = async () => {
        if (pollAttemptsRef.current >= POLL_MAX_ATTEMPTS) {
          stopPolling();
          setIsExporting(false);
          setStatusMessage(null);
          showErrorDialog(
            'AccountingEventClient.getExport',
            new Error(exportTimedOutMsg),
            { trackErrorData: { exportId } }
          );
          return;
        }

        pollAttemptsRef.current += 1;

        let result: AccountingEventExportResult | null = null;
        try {
          result = await client.getExport(exportId);
        } catch (err) {
          stopPolling();
          setIsExporting(false);
          setStatusMessage(null);
          showErrorDialog(
            'AccountingEventClient.getExport',
            err instanceof Error ? err : new Error(String(err)),
            { trackErrorData: { exportId } }
          );
          return;
        }

        if (!result) {
          stopPolling();
          setIsExporting(false);
          setStatusMessage(null);
          return;
        }

        if (result.status === 'completed') {
          stopPolling();
          setIsExporting(false);
          if (result.downloadUrl) {
            triggerDownload(result.downloadUrl);
          } else {
            setStatusMessage(exportNoDownloadUrlMsg);
          }
          setTimeout(() => setStatusMessage(null), 3000);
          return;
        }

        if (result.status === 'failed' || result.status === 'expired') {
          stopPolling();
          setIsExporting(false);
          setStatusMessage(null);
          showErrorDialog(
            'AccountingEventClient.getExport',
            new Error(result.errorMessage ?? exportFailedMsg),
            { trackErrorData: { exportId, status: result.status } }
          );
          return;
        }

        // Still pending or processing — update message and schedule next poll
        const message =
          result.status === 'processing'
            ? exportProcessingMsg
            : exportPreparingMsg;
        setStatusMessage(message);
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      };

      // Kick off first poll after a short delay so the initial "pending" response settles
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    },
    [
      activeAccountWebClientConfig,
      exportFailedMsg,
      exportNoDownloadUrlMsg,
      exportPreparingMsg,
      exportProcessingMsg,
      exportTimedOutMsg,
      showErrorDialog,
      stopPolling,
      triggerDownload,
    ]
  );

  const startExport = useCallback(async () => {
    if (!hasFeature || isExporting) return;

    stopPolling();
    setIsExporting(true);
    setStatusMessage(exportPreparingMsg);

    const client = new AccountingEventClient(activeAccountWebClientConfig);

    let result: AccountingEventExportResult | null = null;
    try {
      result = await client.createExport({
        stream,
        createDateFrom: createDateFrom ?? null,
        createDateTo: createDateTo ?? null,
        effectiveDateFrom: null,
        effectiveDateTo: null,
        entityId: entityId ?? null,
      });
    } catch (err) {
      setIsExporting(false);
      setStatusMessage(null);
      showErrorDialog(
        'AccountingEventClient.createExport',
        err instanceof Error ? err : new Error(String(err)),
        { trackErrorData: { stream, createDateFrom, createDateTo, entityId } }
      );
      return;
    }

    if (!result) {
      setIsExporting(false);
      setStatusMessage(null);
      return;
    }

    if (result.status === 'completed' && result.downloadUrl) {
      setIsExporting(false);
      triggerDownload(result.downloadUrl);
      return;
    }

    if (result.status === 'failed') {
      setIsExporting(false);
      setStatusMessage(null);
      showErrorDialog(
        'AccountingEventClient.createExport',
        new Error(result.errorMessage ?? exportFailedMsg),
        { trackErrorData: { stream } }
      );
      return;
    }

    // Start polling for completion
    pollExport(result.exportId);
  }, [
    hasFeature,
    isExporting,
    stopPolling,
    activeAccountWebClientConfig,
    exportFailedMsg,
    exportPreparingMsg,
    stream,
    createDateFrom,
    createDateTo,
    entityId,
    showErrorDialog,
    triggerDownload,
    pollExport,
  ]);

  return { startExport, isExporting, statusMessage, hasFeature };
};
